import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import { FreeMode, Pagination } from 'swiper/modules';
import { NavLink } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import {
  FiCode,
  FiMonitor,
  FiSmartphone,
  FiPenTool,
  FiTrendingUp,
  FiPhoneCall,
  FiBox,
  FiCheck,
  FiArrowUpRight,
  FiArrowRight,
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import Reveal from './Reveal';
import Footer from './Footer';

const services = [
  {
    icon: FiCode,
    title: 'Software Development',
    desc: 'Custom software built for your workflow — desktop apps, enterprise platforms and complex system integrations engineered to be scalable, secure and fast.',
  },
  {
    icon: FiMonitor,
    title: 'Web Development',
    desc: 'From marketing sites to data-heavy web apps: responsive, accessible and performance-obsessed builds on a modern stack.',
  },
  {
    icon: FiSmartphone,
    title: 'Mobile App Development',
    desc: 'Native-quality iOS and Android apps — intuitive, high-performance and secure, taken from concept to store.',
  },
  {
    icon: FiPenTool,
    title: 'UI/UX Design',
    desc: 'Research-led product design: wireframes, prototypes and polished interfaces that balance aesthetics with real usability.',
  },
  {
    icon: FiTrendingUp,
    title: 'SEO & Social',
    desc: 'Technical SEO, content and paid social that lift visibility, drive qualified traffic and turn attention into conversions.',
  },
  {
    icon: FiPhoneCall,
    title: 'Telecom VAS Integrations',
    desc: 'SMS, IVR, USSD and mobile-payment integrations that keep connectivity smooth and unlock new revenue streams.',
  },
];

const livingClusterFeatures = [
  '0% commission maintenance collection via UPI or card, settled straight to the association bank account',
  'Real-time income and expense ledger every resident can see',
  'QR-based gate and visitor management with delivery approvals',
  'Photo-based complaint tickets, digital notices, RSVPs and voting',
];

const process = [
  { step: '01', title: 'Discover', desc: 'We map goals, users and constraints before a line of code.' },
  { step: '02', title: 'Design', desc: 'Prototypes and flows we validate early, not after launch.' },
  { step: '03', title: 'Build', desc: 'Iterative delivery with clean, tested, documented code.' },
  { step: '04', title: 'Scale', desc: 'Monitoring, optimisation and ongoing product support.' },
];

const testimonials = [
  {
    content:
      'We run an online clinic for intimate health, so trust and privacy are everything. Lotez treated that with the seriousness it deserves - a calm, non-judgemental patient experience, clean booking flows and a tidy integration with our third-party HIPAA-compliant video platform. They took us from concept to a product our patients actually feel safe using.',
    name: 'XnY Health',
    role: 'Healthcare Platform',
    mark: 'XY',
  },
  {
    content:
      'Working with Lotez has been a game-changer for our business. Their team helped us develop a custom software solution that streamlined our operations and improved overall efficiency. The user experience design was spot on, and the product is exactly what we envisioned.',
    name: 'LocalFox',
    role: 'Operations Platform',
    mark: 'LF',
    accent: true,
  },
  {
    content:
      "We approached Lotez to build our SaaS platform for rentals, and we couldn't be happier with the results. They crafted a solution tailored to our complex business needs, integrating advanced technologies seamlessly. The final product exceeded our expectations.",
    name: 'Rentoany',
    role: 'Rentals SaaS Platform',
    mark: 'RA',
  },
];

const Home = () => {
  return (
    <div>
      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-radial-mint" />
        <div className="pointer-events-none absolute inset-0 bg-grid-mint [background-size:64px_64px] [mask-image:radial-gradient(70%_50%_at_50%_0%,black,transparent)]" />

        <div className="container-x relative grid items-center gap-12 pt-36 pb-16 sm:pt-44 sm:pb-20 lg:grid-cols-[1.02fr_.98fr]">
          <div className="relative z-10">
            <Reveal>
              <span className="eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                Full-stack digital partner
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-7 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-7xl">
                Innovating your business with{' '}
                <span className="text-gradient">technology</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-muted sm:text-lg">
                We design and build software that drives growth, efficiency and
                measurable success — turning ambitious ideas into products people
                love to use.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <HashLink
                  to="#services"
                  scroll={(el) =>
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  className="btn-primary w-full sm:w-auto"
                >
                  Get started <FiArrowUpRight className="text-base" />
                </HashLink>
                <NavLink to="/contact" className="btn-ghost w-full sm:w-auto">
                  Contact us
                </NavLink>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-12 flex items-center gap-7 text-sm text-muted">
                {[
                  ['6+', 'Service lines'],
                  ['End-to-end', 'Design to deploy'],
                  ['Long-term', 'Product partnership'],
                ].map(([big, small], i) => (
                  <React.Fragment key={small}>
                    {i > 0 && <span className="h-10 w-px bg-line" />}
                    <span>
                      <strong className="block font-display text-xl font-bold text-ink sm:text-2xl">
                        {big}
                      </strong>
                      {small}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="relative mx-auto w-full max-w-xl">
            <div className="hero-halo" />
            <img
              src="/lotez-tech.jpg"
              alt="Mobile apps, AI, teamwork and success shown through translucent glass panels and glowing data streams"
              width={1024}
              height={1024}
              className="relative aspect-square w-full rounded-[2rem] object-cover shadow-hero"
            />
            <div className="glass-card absolute -bottom-7 left-3 flex items-center gap-3 rounded-2xl p-4 sm:-left-7 sm:w-64">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brandSoft text-brand">
                <FiBox className="text-lg" />
              </span>
              <span>
                <strong className="block font-display text-sm text-ink">
                  Product Development
                </strong>
                <span className="text-xs text-muted">Idea to launch, one team</span>
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== Services ===== */}
      <section id="services" className="section scroll-mt-24">
        <div className="container-x">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="section-kicker">What we do</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-ink sm:text-4xl">
              Our <span className="text-gradient">services</span>
            </h2>
            <p className="mt-4 text-muted">
              We help businesses of every size turn operations into innovative
              digital products.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.06}>
                <article className="service-card h-full">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
                    <s.icon className="text-xl" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-semibold text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted">
                    {s.desc}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Our product ===== */}
      <section id="product" className="section scroll-mt-24">
        <div className="container-x">
          <Reveal>
            <div className="cta-panel grid gap-10 rounded-3xl p-8 sm:p-12 lg:grid-cols-[1fr_1.1fr] lg:p-14">
              <div>
                <span className="section-kicker">Our product</span>
                <h2 className="mt-3 font-display text-3xl font-bold text-ink sm:text-4xl">
                  LivingCluster
                </h2>
                <p className="mt-4 font-display text-lg font-medium text-ink">
                  Your community on auto-pilot.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-muted">
                  We don't only build for clients — we build our own. LivingCluster
                  is a community management platform for Indian apartment
                  associations, putting finances and collections on auto-pilot with
                  open books for every resident, plus gate, complaints, meetings
                  and documents in one place.
                </p>
                <a
                  href="https://livingcluster.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary mt-8"
                >
                  Visit livingcluster.in <FiArrowUpRight className="text-base" />
                </a>
              </div>

              <ul className="grid gap-3 self-center">
                {livingClusterFeatures.map((f) => (
                  <li
                    key={f}
                    className="glass flex items-start gap-3 rounded-2xl p-4 text-sm leading-relaxed text-ink"
                  >
                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-brandSoft text-brand">
                      <FiCheck className="text-sm" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== Process ===== */}
      <section className="section">
        <div className="container-x">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="section-kicker">How we work</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-ink sm:text-4xl">
              A process built for <span className="text-gradient">clarity</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.06}>
                <div className="glass h-full rounded-4xl p-7">
                  <div className="font-display text-sm font-bold text-brand">
                    {p.step}
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-ink">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">
                    {p.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section id="clients" className="section">
        <div className="container-x">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="section-kicker">Client stories</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-ink sm:text-4xl">
              Built around real <span className="text-gradient">outcomes</span>
            </h2>
            <p className="mt-4 text-muted">
              Helping small to large businesses transform their operations into
              digital products.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 overflow-hidden">
          <Swiper
            breakpoints={{
              340: { slidesPerView: 1, spaceBetween: 16 },
              768: { slidesPerView: 2, spaceBetween: 24 },
            }}
            freeMode
            pagination={{ clickable: true }}
            modules={[FreeMode, Pagination]}
            className="!px-5 sm:!px-8 max-w-6xl [&_.swiper-pagination-bullet]:bg-ink/20 [&_.swiper-pagination-bullet-active]:!bg-brand"
          >
            {testimonials.map((item, index) => (
              <SwiperSlide key={index} className="!h-auto pb-14">
                <figure className="glass-card flex h-full flex-col rounded-4xl p-8">
                  <div
                    className={`font-display text-5xl leading-none ${
                      item.accent ? 'text-accent' : 'text-brand'
                    }`}
                  >
                    &ldquo;
                  </div>
                  <blockquote className="mt-2 flex-1 font-display text-lg font-medium leading-relaxed text-ink/90">
                    {item.content}
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-line pt-4">
                    <span
                      className={`client-mark ${
                        item.accent ? 'client-mark-accent' : ''
                      }`}
                    >
                      {item.mark}
                    </span>
                    <span>
                      <strong className="block font-display text-sm font-semibold text-ink">
                        {item.name}
                      </strong>
                      <span className="text-xs text-muted">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="section">
        <div className="container-x">
          <Reveal>
            <div className="cta-panel relative overflow-hidden rounded-[2.5rem] px-6 py-16 text-center sm:px-16">
              <div className="pointer-events-none absolute inset-0 bg-radial-mint" />
              <div className="relative">
                <p className="section-kicker">Ready when you are</p>
                <h2 className="mx-auto mt-3 max-w-2xl font-display text-3xl font-bold text-ink sm:text-5xl">
                  Let's build what moves your{' '}
                  <span className="text-gradient">business next</span>
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-muted">
                  Tell us where you want to go. We'll help you get there.
                </p>
                <motion.div whileHover={{ scale: 1.02 }} className="mt-8 inline-block">
                  <NavLink to="/contact" className="btn-primary">
                    Start a project <FiArrowRight className="text-base" />
                  </NavLink>
                </motion.div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
